<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideCircleX, LucideNewspaper, LucideSearch } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import { EntityNewsSkeletonCard } from '~/components/entity/news/skeleton'
import { EntityNewsCard } from '~/components/entity/news'
import type { TNewsCard } from '~/components/entity/news'
import { Button } from '~/components/ui/button'
import { CategoryPills } from '~/components/ui/category-pills'
import { Input } from '~/components/ui/input'
import type { SearchResponse } from 'meilisearch'
import type { IResponseData } from '~/types'
import type { INewsCategory } from '~/components/entity/news-category'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import type { IOrganization } from '~/components/entity/organization/types'
import type { TResponseNewsList } from '~/components/entity/news/types'

const { $device } = useNuxtApp()
const isMobileClient = ref(false)
const { locale, t } = useI18n()

onMounted(() => {
  isMobileClient.value = $device.isMobile
})

const route = useRoute()
const router = useRouter()

// Track search query state for news
const searchQueryState = useState<string>('searchQuery_news', () => '')

const props = defineProps<{
  organization?: IOrganization
  queryParams?: { per_page?: string }
  eagerLoadCount?: number
}>()

const hitsPerPage = computed(() => Number(props.queryParams?.per_page ?? '30'))
const currentPage = ref<number>(Number(route.query.page || 1))
const searchQueryString = ref(String(route.query.q || ''))
const debouncedQuery = useDebounce(searchQueryString, 500)

// Watch for search query state changes
watch(
  () => searchQueryState.value,
  (newValue) => {
    // Apply value only if it's not empty, has changed, and differs from current search value
    if (newValue && newValue !== searchQueryString.value) {
      searchQueryString.value = newValue
      // Clear state after applying
      searchQueryState.value = ''
    }
  },
  { immediate: true }
)

const config = useRuntimeConfig()
const allNews = ref<TNewsCard[]>([])
const isInitializing = ref(true)

// Flag to skip page refresh when clearing search (let searchKey change handle it)
const skipNextPageRefresh = ref(false)

// source depends on page number
const useApiSearch = computed(() => currentPage.value > 30)

const { data: newsCategories } = props.organization
  ? ref({ data: [] })
  : await useApi<IResponseData<INewsCategory[]>>(
      () => useUrlWithParams('/news_categories?filter[position]=sidebar'),
      // Same key as WidgetNewsCategoriesSidebar, so the request is shared with the sidebar
      { key: `news.categories.sidebar` }
    )
const VISIBLE_CATEGORY_COUNT = 5

// Mirrors the sidebar menu items of WidgetNewsCategoriesSidebar
const categoryMenuItems = computed(() => [
  { title: t('common.economy_news'), url: '/news', icon: 'newspaper' },
  ...(newsCategories?.value?.data ?? []).map((category) => ({
    title: category.title,
    url: category.url,
    icon: category.slug
  })),
  { title: t('common.useful_materials'), url: '/useful-articles', icon: 'info' }
])

// Initialize filters from route params for SSR
const initTagParam = Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag
const initAuthorParam = Array.isArray(route.params.author)
  ? route.params.author[0]
  : route.params.author
const initCatParam = Array.isArray(route.params.category)
  ? route.params.category[0]
  : route.params.category
const { year: initYear, month: initMonth, day: initDay } = route.params

const selectedCategories = ref<{ id: number; label: string; value: string }[]>([])
const selectedTags = ref<string[]>(initTagParam ? [initTagParam] : [])
const selectedAuthorIds = ref<number[]>(
  initAuthorParam ? [Number(initAuthorParam)].filter(Boolean) : []
)
const selectedDates = ref<string[]>(
  initYear && initMonth && initDay
    ? [new Date(Number(initYear), Number(initMonth) - 1, Number(initDay) + 1).toISOString()]
    : []
)

// Resolve category from slug to object (needs newsCategories data)
if (initCatParam && newsCategories?.value) {
  const matched = newsCategories.value.data.find((c) => c.slug === initCatParam)
  if (matched) {
    selectedCategories.value = [{ id: matched.id, label: matched.title, value: matched.slug }]
  }
}

// Keep filters reactive for client-side route changes
watchEffect(() => {
  const catParam = Array.isArray(route.params.category)
    ? route.params.category[0]
    : route.params.category
  if (catParam && newsCategories?.value) {
    const matched = newsCategories.value.data.find((c) => c.slug === catParam)
    if (matched && !selectedCategories.value.find((sc) => sc.id === matched.id)) {
      selectedCategories.value = [{ id: matched.id, label: matched.title, value: matched.slug }]
    }
  }

  const tagParam = Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag
  if (tagParam && newsCategories?.value) {
    selectedTags.value = Array.isArray(tagParam) ? tagParam : [tagParam]
  }

  const authorParam = Array.isArray(route.params.author)
    ? route.params.author[0]
    : route.params.author
  if (authorParam) {
    const ids = Array.isArray(authorParam) ? authorParam.map(Number) : [Number(authorParam)]
    selectedAuthorIds.value = ids.filter(Boolean)
  }

  const { year, month, day } = route.params

  if (year && month && day) {
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day) + 1)
    const isoDate = dateObj.toISOString()
    selectedDates.value = [isoDate]
  }
})

const activeFilter = computed(() => {
  const filters: string[] = []
  if (useCleanRouteName(route.name) === 'useful-articles') {
    filters.push('type = "article"')
  }

  if (props.organization) {
    filters.push(`organizations.slug = ${props.organization.slug}`)
  }

  if (selectedCategories.value.length) {
    const categoryFilters = selectedCategories.value.map((c) => `categories.id = ${c.id}`)
    filters.push(`(${categoryFilters.join(' OR ')})`)
  }

  if (selectedTags.value.length) {
    const tagFilters = selectedTags.value.map((tag) => `tags.name = "${tag}"`)
    filters.push(`(${tagFilters.join(' OR ')})`)
  }

  if (selectedAuthorIds.value.length) {
    const authorFilters = selectedAuthorIds.value.map((id) => `user.id = ${id}`)
    filters.push(`(${authorFilters.join(' OR ')})`)
  }

  if (selectedDates.value.length) {
    const start = selectedDates.value[0]
    const end = new Date(new Date(start).getTime() + 24 * 60 * 60 * 1000).toISOString()
    filters.push(`publication_datetime >= '${start}' AND publication_datetime < '${end}'`)
  }

  return filters.length ? [`(${filters.join(' AND ')})`] : undefined
})

// Compute search key for MeiliSearch
const searchKey = computed(() => {
  if (useApiSearch.value) return null // Don't use SSR for API fallback

  return [
    'news-search',
    debouncedQuery.value,
    selectedCategories.value.map((c) => c.id).join(','),
    selectedTags.value.join(','),
    selectedAuthorIds.value.join(','),
    selectedDates.value.join(','),
    props.organization?.id || 'all',
    useCleanRouteName(route.name)
  ].join('-')
})

// Use useAsyncData for MeiliSearch (pages 1-30)
const {
  data: meiliResult,
  pending: isMeiliSearching,
  error: meiliError,
  refresh: refreshMeili
} = await useAsyncData<SearchResponse<TNewsCard> | null>(
  () => searchKey.value ?? 'news-search-api-fallback',
  async () => {
    if (useApiSearch.value) return null

    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_news`)

    try {
      const res = await search(debouncedQuery.value, {
        hitsPerPage: hitsPerPage.value,
        page: currentPage.value,
        sort: ['weight:desc', 'publication_datetime:desc'],
        attributesToSearchOn: [
          'title',
          'summary',
          'content',
          'user.fullname',
          'categories.title',
          'organizations.title',
          'organizations.official_title'
        ],
        attributesToHighlight: ['title', 'summary'],
        ...(activeFilter.value ? { filter: activeFilter.value } : {})
      })

      return {
        ...res,
        hits: res.hits.map((i) => ({
          ...i,
          title: i._formatted?.title || i.title,
          summary: i._formatted?.summary || i.summary,
          category: i.category?.[locale.value] || ''
        }))
      } as SearchResponse<TNewsCard>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: false,
    watch: false,
    default: () => null
  }
)

// API fallback for pages 31+ (always registered so refresh works after boundary crossing)
const {
  data: apiData,
  pending: isApiPending,
  error: apiError,
  refresh: refreshApi
} = await useAsyncData<TResponseNewsList | null>(
  () =>
    `news-api-${currentPage.value}-${selectedAuthorIds.value.join(',')}-${selectedTags.value.join(',')}-${selectedCategories.value.map((c) => c.id).join(',')}-${selectedDates.value.join(',')}`,
  async () => {
    if (!useApiSearch.value) return null

    const params: Record<string, string | number> = {
      page: currentPage.value,
      per_page: hitsPerPage.value
    }
    if (debouncedQuery.value) params.q = debouncedQuery.value
    if (useCleanRouteName(route.name) === 'useful-articles') params['filter[type]'] = 'article'
    if (props.organization) {
      params['filter[organization_id]'] = props.organization.id
      params['filter[organization_type]'] = props.organization.organization_type
    }
    if (selectedCategories.value.length)
      params['filter[category_id]'] = selectedCategories.value.map((c) => c.id).join(',')
    if (selectedTags.value.length) params['filter[tags]'] = selectedTags.value.join(',')
    if (selectedAuthorIds.value.length)
      params['filter[user_id]'] = selectedAuthorIds.value.join(',')
    if (selectedDates.value.length) params['filter[date]'] = selectedDates.value[0].split('T')[0]

    const { data } = await useApi<TResponseNewsList>(() => useUrlWithParams('/news', params), {
      key: `news-api-ssr-${JSON.stringify(params)}`
    })
    return data.value ?? null
  },
  { server: true, lazy: false, watch: false, default: () => null }
)

const apiResult = computed(() => apiData.value)

// Combined state
const result = computed(() => (useApiSearch.value ? null : meiliResult.value))
const isSearching = computed(() =>
  useApiSearch.value ? isApiPending.value : isMeiliSearching.value
)
const hasError = computed(() => (useApiSearch.value ? !!apiError.value : !!meiliError.value))

// Update allNews based on MeiliSearch results (pages 1-30)
watch(
  meiliResult,
  (newResult) => {
    if (useApiSearch.value) return

    if (!meiliError.value && newResult) {
      if (isMobileClient.value && currentPage.value > 1) {
        allNews.value.push(...(newResult.hits || []))
      } else {
        allNews.value = newResult.hits || []
      }

      if (currentPage.value > 1 && !newResult.hits?.length) {
        showError({ statusCode: 404 })
      }
    } else {
      allNews.value = []
    }
  },
  { immediate: true }
)

// Update allNews based on API results (pages 31+)
watch(
  apiData,
  (newResult) => {
    if (!useApiSearch.value || !newResult) return

    if (isMobileClient.value && currentPage.value > 1) {
      allNews.value.push(...(newResult.data || []))
    } else {
      allNews.value = newResult.data || []
    }

    if (currentPage.value > 1 && !newResult.data?.length) {
      showError({ statusCode: 404 })
    }
  },
  { immediate: true }
)

const loadMore = () => {
  if (!isSearching.value) {
    const totalPages = useApiSearch.value
      ? apiResult.value?.meta?.last_page
      : result.value?.totalPages

    if (totalPages && currentPage.value < totalPages) {
      currentPage.value++
    }
  }
}

const changePage = (page: number) => {
  if (page === currentPage.value) return
  currentPage.value = page

  router.replace({
    query: {
      ...route.query,
      q: searchQueryString.value || undefined,
      page: page > 1 ? String(page) : undefined
    }
  })
}

const clearSearch = () => {
  skipNextPageRefresh.value = true
  searchQueryString.value = ''
  currentPage.value = 1

  router.replace({
    query: {
      ...route.query,
      q: undefined,
      page: undefined
    }
  })
}

watch(debouncedQuery, () => {
  skipNextPageRefresh.value = false
  currentPage.value = 1
  allNews.value = []
  isInitializing.value = false

  router.replace({
    query: {
      ...route.query,
      q: debouncedQuery.value || undefined,
      page: undefined
    }
  })
})

watch(currentPage, (newPage) => {
  if (skipNextPageRefresh.value) {
    skipNextPageRefresh.value = false
    return
  }

  if (!isMobileClient.value || newPage === 1) {
    allNews.value = []
  }

  if (useApiSearch.value) {
    refreshApi()
  } else {
    refreshMeili()
  }
})

onMounted(async () => {
  await nextTick()
  isInitializing.value = false
})

const shouldShowLoadMore = computed(() => {
  if (useApiSearch.value) {
    return (
      apiResult.value &&
      apiResult.value.meta?.last_page &&
      currentPage.value < apiResult.value.meta.last_page
    )
  } else {
    return result.value && result.value.totalPages && currentPage.value < result.value.totalPages
  }
})

const shouldShowPagination = computed(() => {
  if (useApiSearch.value) {
    return apiResult.value?.meta?.last_page && apiResult.value.meta.last_page > 1
  } else {
    return result.value?.totalPages && result.value.totalPages > 1
  }
})

const paginationMeta = computed(() => {
  if (useApiSearch.value) {
    return (
      apiResult.value?.meta || {
        current_page: currentPage.value,
        total: 0,
        per_page: hitsPerPage.value,
        last_page: 1
      }
    )
  } else {
    return {
      current_page: result.value?.page || currentPage.value,
      total: result.value?.totalHits || 0,
      per_page: hitsPerPage.value,
      last_page: result.value?.totalPages || 1
    }
  }
})

const paginationLinks = computed(() => {
  const totalPages = useApiSearch.value
    ? apiResult.value?.meta?.last_page || 1
    : result.value?.totalPages || 1

  return {
    prev:
      currentPage.value > 1 ? `?q=${searchQueryString.value}&page=${currentPage.value - 1}` : null,
    next:
      currentPage.value < totalPages
        ? `?q=${searchQueryString.value}&page=${currentPage.value + 1}`
        : null
  }
})
</script>

<template>
  <div class="space-y-5">
    <div v-if="!organization" class="flex flex-col gap-3">
      <CategoryPills
        :items="categoryMenuItems"
        :sheet-title="$t('common.sections')"
        :visible-count="VISIBLE_CATEGORY_COUNT"
      />

      <div class="relative">
        <Input
          v-model="searchQueryString"
          :placeholder="$t('widget.news_list_meili_search.search_placeholder')"
          class="h-[52px] pl-14 pr-14"
        />
        <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
        <Button
          v-if="searchQueryString"
          size="icon"
          variant="link"
          class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
          :aria-label="$t('widget.news_list_meili_search.clear_search')"
          @click="clearSearch"
        >
          <LucideCircleX />
        </Button>
      </div>
    </div>

    <section v-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>

    <template v-else-if="isMobileClient">
      <div class="space-y-5">
        <div
          v-if="!isSearching && !isInitializing && allNews.length === 0"
          class="flex flex-col items-center gap-2 text-center text-blue-600"
        >
          <LucideNewspaper :size="36" />
          <p>{{ $t('common.no_news') }}</p>
        </div>
        <div class="grid grid-cols-2 gap-3.5 md:grid-cols-3 md:gap-5">
          <EntityNewsCard
            v-for="(card, index) in allNews"
            :key="`${card.id}-${card.publication_datetime}`"
            v-bind="card"
            :eager-loading="props.eagerLoadCount ? index < props.eagerLoadCount : false"
          />
        </div>

        <div v-if="shouldShowLoadMore" class="mt-4 flex justify-center">
          <Button :disabled="isSearching" @click="loadMore">
            <span v-if="isSearching">{{ $t('common.loading_ellipsis') }}</span>
            <span v-else>{{ $t('common.show_more') }}</span>
          </Button>
        </div>

        <div
          v-if="(isSearching || isInitializing) && allNews.length === 0"
          class="grid grid-cols-1 gap-5"
        >
          <EntityNewsSkeletonCard />
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="(!isSearching && !isInitializing) || allNews.length > 0">
        <div
          v-if="allNews.length === 0"
          class="flex flex-col items-center gap-2 text-center text-blue-600"
        >
          <LucideNewspaper :size="36" />
          <p>{{ $t('common.no_news') }}</p>
        </div>

        <div v-else>
          <Heading tag="h2" class="sr-only">{{ $t('common.news') }}</Heading>
          <div class="grid grid-cols-2 gap-3.5 md:grid-cols-3 md:gap-5">
            <EntityNewsCard
              v-for="(card, index) in allNews"
              :key="`${card.id}-${card.publication_datetime}`"
              v-bind="card"
              :eager-loading="props.eagerLoadCount ? index < props.eagerLoadCount : false"
            />
          </div>
        </div>

        <div v-if="shouldShowPagination" class="mt-4 flex justify-end">
          <FeaturePagination :meta="paginationMeta" :links="paginationLinks" @change="changePage" />
        </div>
      </div>

      <div
        v-if="(isSearching || isInitializing) && allNews.length === 0"
        class="grid grid-cols-1 gap-5"
      >
        <EntityNewsSkeletonCard v-for="i in 1" :key="i" />
      </div>
    </template>
  </div>
</template>
